import { FC, useEffect, useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import Swal from "sweetalert2";

import { InsightDay, UserSleepType } from "../../DTO";
import { useAppSelector } from "../../redux-config/hooks";
import { selectWeek } from "../../redux-config/entities/week";
import { selectUser } from "../../redux-config/entities/user";
import { AppUserApi } from "../../api";
import { ReportSleepChart } from "../chart/sleep_chart";
import {
    formatDuration,
    formatPatientDateTime,
} from "../../utils/health-report-format";

export const SleepReport: FC<{ userId: number; days?: InsightDay[] }> = ({
    userId,
    days,
}) => {
    const [loader, setLoader] = useState(false);
    const [sleep, setSleep] = useState<UserSleepType[]>([]);
    const week = useAppSelector(selectWeek);
    const user = useAppSelector(selectUser);

    useEffect(() => {
        setLoader(true);
        AppUserApi.UserSleep(user, Number(userId), week.index)
            .then((data) => setSleep(data.sleep))
            .catch((error) => Swal.fire({ icon: "error", text: error }))
            .finally(() => setLoader(false));
    }, [user, userId, week.index]);

    if (loader)
        return (
            <div className="health-report-loading">
                <Spinner animation="border" size="sm" /> Loading sleep…
            </div>
        );

    return (
        <div className="health-report-stack">
            <section className="health-report-panel">
                <header className="health-report-heading">
                    <div>
                        <span>Recorded sleep</span>
                        <h2>Sleep sessions</h2>
                    </div>
                    <p>
                        One clear patient-local time is shown instead of
                        separate UTC and PDT values.
                    </p>
                </header>
                {sleep.length ? (
                    <Table
                        responsive
                        className="health-report-table align-middle"
                    >
                        <thead>
                            <tr>
                                <th>Sleep date</th>
                                <th>Went to bed</th>
                                <th>Woke up</th>
                                <th>Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sleep.map((item) => {
                                const start = formatPatientDateTime(
                                    item.startDate,
                                    item.timezoneOffset,
                                );
                                const end = formatPatientDateTime(
                                    item.endDate,
                                    item.timezoneOffset,
                                );
                                const seconds =
                                    (new Date(item.endDate).getTime() -
                                        new Date(item.startDate).getTime()) /
                                    1000;
                                return (
                                    <tr key={item.id}>
                                        <td>{start.date}</td>
                                        <td>
                                            <strong>{start.time}</strong>
                                            <small>Patient local time</small>
                                        </td>
                                        <td>
                                            <strong>{end.time}</strong>
                                            <small>{end.date}</small>
                                        </td>
                                        <td>
                                            <strong>
                                                {formatDuration(seconds)}
                                            </strong>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                ) : (
                    <div className="health-report-empty">
                        <strong>No sleep recorded</strong>
                        <span>This week has no sleep sessions yet.</span>
                    </div>
                )}
            </section>
            <ReportSleepChart sleep={sleep} days={days} />
        </div>
    );
};
